import { z } from "zod";
import { advisorPairingIssue } from "./advisor-pairing.js";
import { ModelIdSchema } from "./models.js";
/**
 * The multi-agent block of a template, in Anthropic Managed Agents' OWN shape
 * (owner decision 2026-10-11: the Managed Agents `multiagent` field is the
 * source of truth for V5's multi-agent format, on every provider). Field
 * names, entry forms and the structural rules below are the provider's. Two
 * things are V5's: an agent id is a template reference `<library_key>/<slug>`
 * within the same site (never a provider agent id — those never enter a
 * template), and an authored `version` is refused by the roster check
 * (`server/src/catalog/roster.ts`, at publish and on a project draft). V5 does
 * not pin a member's version: each member resolves to its template's latest
 * live version when a run starts, and the engine drops any version it is given.
 *
 * On Anthropic the block compiles to the provider field as written (template
 * references become provisioned agent versions). On Foundry and OpenRouter
 * the orchestrator executes the same declaration (the loop since #332, #340 and
 * #342; the sandbox service since the microVM plan's waves 0 to 3), and `blocks.schema`
 * answers each pair's support from the checker's seat rule, never from a table.
 *
 * Two variants, exactly as Anthropic documents them:
 * - `coordinator`: a roster of 1–20 entries; the advisor is a roster entry.
 * - `multiagent_20261001`: `subagents` / `workflows` / `advisor` members. The
 *   roster is `subagents.predefined_agents`; dynamic workflows and delegation
 *   are ON unless the member is `{ type: disabled }`; the advisor is only the
 *   `advisor` member, never a predefined-agent entry.
 */
/**
 * The `ant__` custom-tool prefix is reserved by Anthropic for its workflow tooling (an agent
 * carrying one is refused with a 400 at save and at session create). V5 refuses it by the one
 * rule below on the wire (`CustomToolSchema`), on an agent.yaml custom tool (the spec checker's
 * schema, `server/src/execution/spec/spec.ts`), at a project draft save (the draft gate,
 * `server/src/projects/project-service.ts`, 422 `tool_name_reserved`) and on the façade's
 * `/v1/agents` (`server/src/cma/store/multiagent.ts`).
 */
export const ANT_TOOL_PREFIX = "ant__";
/** Why a custom tool's name is refused (it wears the reserved prefix), or null when the name is free. */
export function reservedToolNameIssue(name) {
    return name.startsWith(ANT_TOOL_PREFIX)
        ? `custom tool "${name}": the ${ANT_TOOL_PREFIX} prefix is reserved; rename or remove the tool`
        : null;
}
/**
 * The first custom tool of a parsed `tools` list (any shape: an agent.yaml document, a façade
 * body) whose name wears the reserved prefix, or null when none does or the list is not one.
 */
export function reservedCustomTool(tools) {
    if (!Array.isArray(tools))
        return null;
    for (const [index, tool] of tools.entries()) {
        if (typeof tool !== "object" || tool === null || Array.isArray(tool))
            continue;
        const { type, name } = tool;
        if (type !== "custom" || typeof name !== "string")
            continue;
        const issue = reservedToolNameIssue(name);
        if (issue)
            return { name, index, issue };
    }
    return null;
}
/** A template reference: `<library_key>/<slug>` of a template in the same site. */
export const TEMPLATE_REF_PATTERN = /^[a-z0-9]+(?:[_-][a-z0-9]+)*\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const TemplateRefSchema = z
    .string()
    .max(161)
    .regex(TEMPLATE_REF_PATTERN, "an agent id is <library_key>/<slug> of a template in the same site");
export const MULTIAGENT_ROSTER_MAX = 20;
export const RosterAgentRefSchema = z.strictObject({
    type: z.literal("agent"),
    id: TemplateRefSchema,
    /**
     * Anthropic pins a stored agent version here. V5 does not: the member
     * resolves to its template's latest live version at run start and the engine
     * drops a version, so the roster check refuses an authored value (at publish
     * and on a project draft) rather than letting it be ignored.
     */
    version: z.number().int().positive().optional(),
});
export const RosterSelfSchema = z.strictObject({ type: z.literal("self") });
export const RosterAdvisorSchema = z.strictObject({
    type: z.literal("advisor"),
    model: ModelIdSchema,
});
/** A roster entry: the string shorthand (latest version of the agent), an agent reference, `self`, or the advisor. */
export const RosterEntrySchema = z.union([
    TemplateRefSchema,
    RosterAgentRefSchema,
    RosterSelfSchema,
    RosterAdvisorSchema,
]);
/** `predefined_agents` lists take every roster form except the advisor. */
export const PredefinedAgentEntrySchema = z.union([
    TemplateRefSchema,
    RosterAgentRefSchema,
    RosterSelfSchema,
]);
/**
 * The rules one roster list obeys: no agent twice, `self` at most once, at most one advisor.
 * Exported with `multiagent20261001Issues` for the spec checker's own schema
 * (`server/src/execution/spec/spec.ts`), which names members by any id its `--roster` maps.
 */
export function rosterIssues(entries, path, context) {
    const seen = new Set();
    let selves = 0;
    let advisors = 0;
    entries.forEach((entry, index) => {
        if (typeof entry === "string" || entry.type === "agent") {
            const id = typeof entry === "string" ? entry : entry.id;
            if (seen.has(id)) {
                context.addIssue({
                    code: "custom",
                    path: [...path, index],
                    message: `duplicate agent ${id}`,
                });
            }
            seen.add(id);
        }
        else if (entry.type === "self") {
            selves += 1;
            if (selves > 1) {
                context.addIssue({
                    code: "custom",
                    path: [...path, index],
                    message: "self is listed more than once",
                });
            }
        }
        else {
            advisors += 1;
            if (advisors > 1) {
                context.addIssue({
                    code: "custom",
                    path: [...path, index],
                    message: "at most one advisor per roster",
                });
            }
        }
    });
}
export const CoordinatorMultiagentSchema = z
    .strictObject({
    type: z.literal("coordinator"),
    agents: z.array(RosterEntrySchema).min(1).max(MULTIAGENT_ROSTER_MAX),
})
    .superRefine((block, context) => rosterIssues(block.agents, ["agents"], context));
const ToggleSchema = z.strictObject({ type: z.enum(["enabled", "disabled"]) });
/** `subagents` and `workflows` share one setting shape: a roster plus whether the model may define agents of its own. */
export const AgentListSettingSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("enabled"),
        predefined_agents: z.array(PredefinedAgentEntrySchema).max(MULTIAGENT_ROSTER_MAX).optional(),
        /** Default enabled: the model may delegate to (or plan with) agents it defines itself. */
        inline_agents: ToggleSchema.optional(),
    }),
    z.strictObject({ type: z.literal("disabled") }),
]);
export const AdvisorSettingSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("enabled"), model: ModelIdSchema }),
    z.strictObject({ type: z.literal("disabled") }),
]);
/**
 * The rules of a `multiagent_20261001` block beyond its shape: each list obeys `rosterIssues`,
 * a list whose `inline_agents` is disabled names at least one agent, and a block that turns
 * everything off is refused.
 */
export function multiagent20261001Issues(block, context) {
    for (const member of ["subagents", "workflows"]) {
        const setting = block[member];
        if (!setting || setting.type === "disabled")
            continue;
        rosterIssues(setting.predefined_agents ?? [], [member, "predefined_agents"], context);
        if (setting.inline_agents?.type === "disabled" && !setting.predefined_agents?.length) {
            context.addIssue({
                code: "custom",
                path: [member, "predefined_agents"],
                message: `${member}.predefined_agents must list at least one agent when inline_agents is disabled`,
            });
        }
    }
    if (block.subagents?.type === "disabled" &&
        block.workflows?.type === "disabled" &&
        block.advisor?.type !== "enabled") {
        context.addIssue({
            code: "custom",
            message: "the block disables subagents and workflows and has no advisor — remove it",
        });
    }
}
export const Multiagent20261001Schema = z
    .strictObject({
    type: z.literal("multiagent_20261001"),
    subagents: AgentListSettingSchema.optional(),
    workflows: AgentListSettingSchema.optional(),
    advisor: AdvisorSettingSchema.optional(),
})
    .superRefine(multiagent20261001Issues);
export const MultiagentSchema = z.discriminatedUnion("type", [
    CoordinatorMultiagentSchema,
    Multiagent20261001Schema,
]);
function refsOf(entries, base) {
    const agents = [];
    let self = false;
    entries.forEach((entry, index) => {
        if (typeof entry === "string")
            agents.push({ id: entry, path: [...base, index] });
        else if (entry.type === "agent") {
            agents.push({
                id: entry.id,
                ...(entry.version === undefined ? {} : { version: entry.version }),
                path: [...base, index],
            });
        }
        else if (entry.type === "self")
            self = true;
    });
    return { agents, self };
}
export function rosterView(block) {
    if (block.type === "coordinator") {
        const { agents, self } = refsOf(block.agents, ["agents"]);
        const advisorIndex = block.agents.findIndex((entry) => typeof entry !== "string" && entry.type === "advisor");
        const advisor = advisorIndex === -1 ? null : block.agents[advisorIndex];
        return {
            agents,
            self,
            advisorModel: advisor && typeof advisor !== "string" && advisor.type === "advisor" ? advisor.model : null,
            advisorPath: advisorIndex === -1 ? null : ["agents", advisorIndex],
            subagents: agents.length > 0 || self,
            workflows: false,
            inlineAgents: false,
        };
    }
    const subagents = block.subagents?.type !== "disabled";
    const workflows = block.workflows?.type !== "disabled";
    const inlineOf = (setting) => setting?.type !== "disabled" && setting?.inline_agents?.type !== "disabled";
    const listOf = (setting) => setting?.type === "enabled" ? (setting.predefined_agents ?? []) : [];
    const rostered = refsOf(listOf(block.subagents), ["subagents", "predefined_agents"]);
    const planned = refsOf(listOf(block.workflows), ["workflows", "predefined_agents"]);
    return {
        agents: [...rostered.agents, ...planned.agents],
        self: rostered.self || planned.self,
        advisorModel: block.advisor?.type === "enabled" ? block.advisor.model : null,
        advisorPath: block.advisor?.type === "enabled" ? ["advisor"] : null,
        subagents,
        workflows,
        inlineAgents: (subagents && inlineOf(block.subagents)) || (workflows && inlineOf(block.workflows)),
    };
}
/**
 * The one pairing rule (`advisor-pairing.ts`) over a block's advisor and the agent's own model:
 * the reason the advisor tool's table refuses the pair, or null when the block has no advisor or
 * the table accepts or does not rank the pair. Claude refuses an agent whose pair is invalid
 * with a 400 at save, so the draft gate (422 `roster_advisor_pairing`) and the spec checker
 * refuse it first, on every provider.
 */
export function advisorPairingIssueOf(agentModel, block) {
    if (!block)
        return null;
    const view = rosterView(block);
    if (view.advisorModel === null || view.advisorPath === null)
        return null;
    const reason = advisorPairingIssue(agentModel, view.advisorModel);
    return reason ? { reason, path: [...view.advisorPath, "model"] } : null;
}
/**
 * The runtime capabilities a block implies — derived, never authored in
 * `requirements`. The capability table says how each provider delivers them
 * (native on Anthropic; `none` on the loops until the orchestrator lands) and
 * readiness refuses a pair that lacks one. The provider of the pair is the
 * root's: every member and the advisor run on the root's engine (the spec
 * router resolves them there, at the gate as at run time).
 */
export const MULTIAGENT_CAPABILITY_IDS = [
    "agents.subagents",
    "agents.advisor",
    "agents.workflows",
    "agents.inline",
];
export function multiagentCapabilities(block) {
    if (!block)
        return [];
    const view = rosterView(block);
    const out = [];
    if (view.subagents)
        out.push("agents.subagents");
    if (view.advisorModel !== null)
        out.push("agents.advisor");
    if (view.workflows)
        out.push("agents.workflows");
    if (view.inlineAgents)
        out.push("agents.inline");
    return out;
}
//# sourceMappingURL=multiagent.js.map